import React from 'react'

import { Controller } from '@hotwired/stimulus'
import { createRoot } from 'react-dom/client'

const components = import.meta.glob('../components/**/*.{js,jsx}')

// Build a flat map of component names to dynamic import functions
const componentMap = {}
Object.keys(components).forEach(path => {
  // Extract component name from path: '../components/Foo/Bar.jsx' -> 'Foo/Bar'
  let name = path.replace('../components/', '').replace(/\.(jsx?|tsx?)$/, '')
  const importFn = components[path]
  componentMap[name] = importFn

  // Also register index files without '/index' suffix for backwards compatibility
  // '../components/ChangeLog/index.jsx' -> 'ChangeLog'
  if (name.endsWith('/index')) {
    const shortName = name.replace('/index', '')
    componentMap[shortName] = importFn
  }
})

const componentCache = new Map()

const loadComponent = (componentName, importFn) => {
  if (!componentCache.has(componentName)) {
    componentCache.set(
      componentName,
      importFn().then(module => module.default || module)
    )
  }

  return componentCache.get(componentName)
}

const fallback = React.createElement(
  'div',
  { className: 'd-flex justify-content-center align-items-center p-3 text-muted' },
  React.createElement('i', { className: 'fa fa-spinner fa-spin' })
)

export default class extends Controller {
  connect() {
    const componentName = this.element.getAttribute('data-react-class')
    const propsJson = this.element.getAttribute('data-react-props')
    const importFn = componentMap[componentName]

    if (!importFn) {
      console.error(`React component not found: ${componentName}`)
      console.log('Available components:', Object.keys(componentMap))
      return
    }

    const props = propsJson ? JSON.parse(propsJson) : {}
    const root = createRoot(this.element)

    this.root = root
    root.render(fallback)

    loadComponent(componentName, importFn)
      .then(Component => {
        if (this.root === root) root.render(React.createElement(Component, props))
      })
      .catch(error => {
        if (this.root === root) root.render(null)
        throw error
      })
  }

  disconnect() {
    // Unmount React component when Turbo navigates away
    if (this.root) {
      this.root.unmount()
      this.root = null
    }
  }
}
